import { useEffect, useRef, type ReactNode } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router';

/** Delegated pointer lighting avoids a React render on every mouse movement. */
export function PageMotion({ children }: { children: ReactNode }) {
  const root = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (
      navigationType !== NavigationType.Pop &&
      !pathname.startsWith('/stories/') &&
      !window.location.hash
    ) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [pathname, navigationType]);

  useEffect(() => {
    const element = root.current;
    if (!element || typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(prefers-reduced-motion: no-preference) and (pointer: fine)');
    let active: HTMLElement | null = null;
    let frame = 0;
    let x = 0;
    let y = 0;
    function reset() {
      active?.style.removeProperty('--tilt-x');
      active?.style.removeProperty('--tilt-y');
      active?.style.removeProperty('--light-x');
      active?.style.removeProperty('--light-y');
      active = null;
    }
    function move(event: PointerEvent) {
      if (!media.matches || document.documentElement.dataset.motion === 'paused') {
        reset();
        return;
      }
      const card =
        event.target instanceof Element ? event.target.closest<HTMLElement>('[data-tilt]') : null;
      if (card !== active) {
        reset();
        active = card;
      }
      x = event.clientX;
      y = event.clientY;
      if (frame || !active) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (!active) return;
        const rect = active.getBoundingClientRect();
        const px = Math.max(0, Math.min(1, (x - rect.left) / rect.width));
        const py = Math.max(0, Math.min(1, (y - rect.top) / rect.height));
        active.style.setProperty('--tilt-x', `${(0.5 - py) * 5}deg`);
        active.style.setProperty('--tilt-y', `${(px - 0.5) * 5}deg`);
        active.style.setProperty('--light-x', `${px * 100}%`);
        active.style.setProperty('--light-y', `${py * 100}%`);
      });
    }
    element.addEventListener('pointermove', move, { passive: true });
    element.addEventListener('pointerleave', reset);
    media.addEventListener('change', reset);
    return () => {
      cancelAnimationFrame(frame);
      reset();
      element.removeEventListener('pointermove', move);
      element.removeEventListener('pointerleave', reset);
      media.removeEventListener('change', reset);
    };
  }, [pathname]);

  return (
    <div
      ref={root}
      key={pathname.startsWith('/stories/') ? 'story' : pathname}
      className={pathname.startsWith('/stories/') ? undefined : 'page-enter'}
    >
      {children}
    </div>
  );
}
